import * as React from 'react';
import { MapPin, Trash2, CalendarRange, PlusSquare, Plus } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@ooh/ui/components/button';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import { ColumnPicker, type ColumnPickerColumn } from '@ooh/ui/components/column-picker';
import { SELECT_COLUMN_ID } from '@ooh/screens';
import { ImportMenu } from './page-states';

// Structural 'select' checkbox column is never user-toggleable/orderable in the picker.
const PICKER_PINNED_IDS = [SELECT_COLUMN_ID];

interface InventoryToolbarRowProps {
  /** Rendered before every other action — e.g. the conditional "Zapisz do kampanii" link. */
  leadingSlot?: React.ReactNode;

  onOpenMap: () => void;
  mapLabel: string;

  /** Renders the "Kosz" (trash) icon link only when true (mirrors media_unit.edit gating). */
  canEdit: boolean;
  trashLabel: string;

  showMonthColumns: boolean;
  onToggleMonthColumns: () => void;
  monthToggleLabel: string;

  columns: ColumnPickerColumn[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggleColumn: (id: string) => void;
  columnToggleLabel: string;
  columnToggleSectionLabel: string;
  columnSearchPlaceholder: string;
  columnNoResults: string;
  onMoveColumnUp?: (id: string) => void;
  onMoveColumnDown?: (id: string) => void;

  /** Renders the "Dodaj do oferty" quick-add button only when true. */
  canQuickAddToOffer: boolean;
  quickAddLabel: string;
  onQuickAdd: () => void;

  canImport?: boolean;
  canImportAvailability?: boolean;
  importMenuLabel?: string;
  importMediaLabel?: string;
  importAvailabilityLabel?: string;

  /** Renders the "Dodaj nośnik" primary button only when true (media_unit.create). */
  canCreate: boolean;
  addButtonLabel: string;
  onAddNew: () => void;
}

/**
 * Single dense, right-aligned action row rendered directly below the search/filter
 * row and directly above the DataTable (and above `AvailabilitySubToolbar` when the
 * availability view is on). Replaces the old 3-row structure (a separate
 * `InventoryActionsRow` above the filters, plus `MediaInventoryResultsView` +
 * `ViewSwitcherControls` above the table) — see
 * `apps/mockups/src/screens/media-inventory.mockup.tsx`'s "table-action-row".
 *
 * Four icon-only buttons (Mapa, Kosz, Dostępność, Kolumny) share identical chrome via
 * `IconButtonWithTooltip` / `ColumnPicker`'s `triggerSize="icon-sm"`, followed by up to
 * three icon+text buttons (Dodaj do oferty, Importuj z pliku — outline; Dodaj nośnik —
 * primary).
 */
export function InventoryToolbarRow({
  leadingSlot,
  onOpenMap,
  mapLabel,
  canEdit,
  trashLabel,
  showMonthColumns,
  onToggleMonthColumns,
  monthToggleLabel,
  columns,
  hiddenColumnIds,
  onToggleColumn,
  columnToggleLabel,
  columnToggleSectionLabel,
  columnSearchPlaceholder,
  columnNoResults,
  onMoveColumnUp,
  onMoveColumnDown,
  canQuickAddToOffer,
  quickAddLabel,
  onQuickAdd,
  canImport = false,
  canImportAvailability = false,
  importMenuLabel,
  importMediaLabel,
  importAvailabilityLabel,
  canCreate,
  addButtonLabel,
  onAddNew,
}: InventoryToolbarRowProps) {
  return (
    <div className="mt-3 flex items-center justify-end gap-2" data-testid="table-action-row">
      {leadingSlot}
      <IconButtonWithTooltip
        icon={MapPin}
        tooltip={mapLabel}
        onClick={onOpenMap}
        testId="show-on-map-button"
      />
      {canEdit ? (
        <IconButtonWithTooltip asChild tooltip={trashLabel} testId="trash-link">
          <Link to="/media-units/trash" aria-label={trashLabel}>
            <Trash2 className="h-4 w-4" />
          </Link>
        </IconButtonWithTooltip>
      ) : null}
      <IconButtonWithTooltip
        icon={CalendarRange}
        tooltip={monthToggleLabel}
        active={showMonthColumns}
        onClick={onToggleMonthColumns}
        testId="toggle-month-columns"
      />
      <ColumnPicker
        columns={columns}
        pinnedIds={PICKER_PINNED_IDS}
        hiddenColumnIds={hiddenColumnIds}
        onToggle={onToggleColumn}
        label={columnToggleLabel}
        sectionLabel={columnToggleSectionLabel}
        searchPlaceholder={columnSearchPlaceholder}
        noResults={columnNoResults}
        onMoveUp={onMoveColumnUp}
        onMoveDown={onMoveColumnDown}
        triggerSize="icon-sm"
      />
      {canQuickAddToOffer ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onQuickAdd}
          data-testid="quick-add-results-button"
        >
          <PlusSquare className="h-4 w-4" />
          {quickAddLabel}
        </Button>
      ) : null}
      <ImportMenu
        canImport={canImport}
        canImportAvailability={canImportAvailability}
        importMenuLabel={importMenuLabel}
        importMediaLabel={importMediaLabel}
        importAvailabilityLabel={importAvailabilityLabel}
      />
      {canCreate ? (
        <Button type="button" size="sm" onClick={onAddNew} data-testid="add-media-unit-button">
          <Plus className="h-4 w-4" />
          {addButtonLabel}
        </Button>
      ) : null}
    </div>
  );
}
